import React, { useState, useEffect } from 'react';
import ApiService from '../../service/ApiService';
import { apiPaths } from '../../service/apiPaths';

interface JibbleModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (status: 'IN' | 'OUT', activityId?: string, note?: string) => void;
  defaultStatus?: 'IN' | 'OUT';
  confirmationMode?: boolean;
  confirmationMessage?: string;
}

const JibbleModal: React.FC<JibbleModalProps> = ({ 
  open, 
  onClose, 
  onSubmit, 
  defaultStatus,
  confirmationMode = false,
  confirmationMessage = 'Are you sure you want to set Jibble status to OUT?'
}) => {
  const [status, setStatus] = useState<'IN' | 'OUT'>('IN');
  const [note, setNote] = useState<string>('Working');
  const [activities, setActivities] = useState<Array<{ id: string; name: string; code?: string }>>([]);
  const [selectedActivityId, setSelectedActivityId] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      const initialStatus = defaultStatus || (localStorage.getItem('jibbleStatusSelection') as 'IN' | 'OUT') || 'IN';
      setStatus(initialStatus);
      setNote('Working');
      setSelectedActivityId('');
      setIsSubmitting(false); // Reset loading state when modal opens
      // Load activities for dropdown
      ApiService({ method: 'GET', endpoint: apiPaths.jibbleActivities })
        .then((res) => {
          if (Array.isArray(res)) setActivities(res);
        })
        .catch(() => {
          setActivities([]);
        });
    }
  }, [open, defaultStatus]);

  const handleYesClick = async () => {
    setIsSubmitting(true);
    try {
      await onSubmit(status, selectedActivityId || undefined, note.trim() || undefined);
    } finally {
      // Note: The loading state will be reset when the modal reopens
      // because setIsSubmitting(false) is in the useEffect
    }
  };

  if (!open) return null;

  if (confirmationMode) {
    return (
      <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40">
        <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
          <h2 className="text-lg font-semibold text-fontBlack mb-4">Confirm Jibble Status</h2>
          <div className="mb-6">
            <p className="text-base text-fontColor mb-2">{confirmationMessage}</p>
            <p className="text-sm text-gray-600">Status: <span className="font-semibold">{status}</span></p>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 rounded-md border border-border text-fontBlack bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              No
            </button>
            <button
              type="button"
              onClick={handleYesClick}
              disabled={isSubmitting}
              className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center gap-2 min-w-[80px] justify-center"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Loading...</span>
                </>
              ) : (
                'Yes'
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Regular modal mode (non-confirmation)
  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
        <h2 className="text-lg font-semibold text-fontBlack mb-4">Set Jibble Status </h2>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-fontBlack mb-2">Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as 'IN' | 'OUT')}
            className="w-full border border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="IN">IN</option>
            <option value="OUT">OUT</option>
          </select>
        </div>

        {status === 'IN' && activities.length > 0 && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-fontBlack mb-2">Activity</label>
            <select
              value={selectedActivityId}
              onChange={(e) => setSelectedActivityId(e.target.value)}
              className="w-full border border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Activity</option>
              {activities.map((activity) => (
                <option key={activity.id} value={activity.id}>
                  {activity.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="mb-6">
          <label className="block text-sm font-medium text-fontBlack mb-2">Note</label>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full border border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter note..."
          />
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 rounded-md border border-border text-fontBlack bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleYesClick}
            disabled={isSubmitting}
            className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center gap-2 min-w-[100px] justify-center"
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Loading...</span>
              </>
            ) : (
              'Submit'
            )}
          </button>
        </div>
      </div>
    </div>
  );


  // return (
  //   <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40">
  //     <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
  //       <h2 className="text-lg font-semibold text-fontBlack mb-4">Jibble Status</h2>
  //       <div className="mb-4">
  //         <label className="block text-sm mb-1 text-fontColor">Select status 1</label>
  //         <select
  //           value={status}
  //           onChange={(e) => {
  //             const newStatus = e.target.value as 'IN' | 'OUT';
  //             setStatus(newStatus);
  //           }}
  //           className="w-full border border-border rounded-md p-2 text-fontBlack bg-white"
  //         >
  //           <option value="IN">IN</option>
  //           <option value="OUT">OUT</option>
  //         </select>
  //       </div>
  //       {/* {status === 'IN' ? (
  //         <div className="mb-4">
  //           <label className="block text-sm mb-1 text-fontColor">Activity (optional)</label>
  //           <select
  //             value={selectedActivityId}
  //             onChange={(e) => setSelectedActivityId(e.target.value)}
  //             className="w-full border border-border rounded-md p-2 text-fontBlack bg-white"
  //           >
  //             <option value="">Select activity (optional)</option>
  //             {activities.map((a) => (
  //               <option key={a.id} value={a.id}>
  //                 {a.name}
  //               </option>
  //             ))}
  //           </select>
  //         </div>
  //       ) : null} */}
  //       <div className="mb-6">
  //         <label className="block text-sm mb-1 text-fontColor">Note (optional)</label>
  //         <select
  //           value={note}
  //           onChange={(e) => setNote(e.target.value)}
  //           className="w-full border border-border rounded-md p-2 text-fontBlack bg-white"
  //         >
  //           <option value="Working">Working</option>
  //           <option value="Break">Break</option>
  //           <option value="Day Off">Day Off</option>
  //         </select>
  //       </div>
  //       <div className="flex justify-end gap-2">
  //         <button
  //           type="button"
  //           onClick={onClose}
  //           className="px-4 py-2 rounded-md border border-border text-fontBlack bg-white hover:bg-gray-50"
  //         >
  //           Cancel
  //         </button>
  //         <button
  //           type="button"
  //           onClick={() => onSubmit(status, selectedActivityId || undefined, note.trim() || undefined)}
  //           className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
  //         >
  //           Submit
  //         </button>
  //       </div>
  //     </div>
  //   </div>
  // );
};

export default JibbleModal;


